import { type KeyboardEvent, useEffect, useRef, useState } from "react";
import { useProgressStore } from "@/application/stores/progressStore";
import { TOTAL_LEVELS } from "@/domain/progress/model/progress";
import { Button } from "@/presentation/components/Button";
import { Icon } from "@/presentation/components/icons/Icon";
import { MON_BLANK_NAME } from "@/presentation/components/icons/icon-names";
import { HeaderZone, MainZone } from "@/presentation/components/layout/Zones";
import { useOverlay } from "@/presentation/components/overlay/OverlayProvider";
import { useBgm } from "@/presentation/features/bgm/useBgm";
import { SettingsPanel } from "@/presentation/features/settings/SettingsPanel";
import { LABELS } from "@/presentation/i18n/labels";
import type { Route } from "@/presentation/router/route";
import { useRouter } from "@/presentation/router/useRouter";
import "./StampBook.css";
import { buildStampFrames, findInitialFocusIndex, type StampFrame } from "./stamp-frames";

/** 4×2グリッドの列数（1行目level-1〜4、2行目level-5〜7+免許皆伝）。 */
const GRID_COLUMNS = 4;

interface StampBookProps {
  /**
   * ルータへの遷移要求を差し替えるためのフック（主にテスト用）。
   * 省略時は`useRouter`の`navigate`をそのまま使う。
   */
  readonly onNavigate?: (route: Route) => void;
}

function frameAriaLabel(frame: StampFrame): string {
  if (frame.levelId === null) {
    return frame.earned ? `${frame.label}・達成` : `${frame.label}・未達成`;
  }
  return frame.earned ? `${frame.label}・獲得済み` : `${frame.label}・未獲得。この段へ進む`;
}

/**
 * S-3 免状帳（`/stamps`）。石版台紙 + 朱印の枠×8（4×2グリッド。screen-flow.md §8.3）。
 * 達成を見せつつ、未獲得枠を押せば即座にその段へ飛べる（N-006。閲覧の行き止まりを作らない）。
 */
export function StampBook({ onNavigate }: StampBookProps) {
  const router = useRouter();
  const navigate = onNavigate ?? router.navigate;
  const progress = useProgressStore((state) => state.progress);
  const frames = buildStampFrames(progress);
  const { openPopover } = useOverlay();
  useBgm("gate");

  // 初期フォーカス先（最初の未獲得枠。全制覇なら免許皆伝枠）は、マウント時点の進捗から
  // 一度だけ決める。以後の進捗更新でフォーカスを奪わない。
  const initialFocusIndexRef = useRef<number>(findInitialFocusIndex(frames));
  const [focusedIndex, setFocusedIndex] = useState<number>(initialFocusIndexRef.current);
  const buttonRefs = useRef<Array<HTMLButtonElement | null>>([]);

  useEffect(() => {
    buttonRefs.current[initialFocusIndexRef.current]?.focus();
  }, []);

  const earnedLevelCount = frames.filter((frame) => frame.levelId !== null && frame.earned).length;
  const grandEarned = frames.find((frame) => frame.id === "grand")?.earned ?? false;

  const statusText = grandEarned
    ? "全制覇です。すべての朱印を授かりました。"
    : earnedLevelCount === 0
      ? "まだ朱印がありません。まずは一段から挑みましょう。"
      : `${earnedLevelCount} / ${TOTAL_LEVELS} 個の朱印`;

  function focusFrame(nextIndex: number) {
    const clamped = Math.min(Math.max(nextIndex, 0), frames.length - 1);
    setFocusedIndex(clamped);
    buttonRefs.current[clamped]?.focus();
  }

  function handleFrameActivate(frame: StampFrame) {
    if (frame.levelId !== null) {
      navigate({ name: "level", levelId: frame.levelId });
    }
  }

  function handleFrameKeyDown(event: KeyboardEvent<HTMLButtonElement>, index: number) {
    switch (event.key) {
      case "ArrowRight":
        event.preventDefault();
        focusFrame(index + 1);
        return;
      case "ArrowLeft":
        event.preventDefault();
        focusFrame(index - 1);
        return;
      case "ArrowDown":
        event.preventDefault();
        focusFrame(index + GRID_COLUMNS);
        return;
      case "ArrowUp":
        event.preventDefault();
        focusFrame(index - GRID_COLUMNS);
        return;
      case "Escape":
        event.preventDefault();
        /*
         * この打鍵はここで使い切る（D-119）。React の合成イベントは root で受けるため、
         * 止めないと同じ Escape がそのまま document まで上がっていく。遷移先の
         * 稽古の段（S-1）は document で Escape を購読しており（門へ戻る）、
         * その購読は discrete イベントの同期フラッシュでこの打鍵の途中に張られるため、
         * 1度の Escape で S-3 → S-1 → S-0 と2つ戻ってしまっていた。
         */
        event.stopPropagation();
        navigate({ name: "level-select" });
        return;
      default:
        return;
    }
  }

  return (
    <>
      <HeaderZone>
        <button
          type="button"
          className="stamp-book__header-btn"
          onClick={() => navigate({ name: "level-select" })}
        >
          <Icon name="back" decorative size={20} />
          <span>稽古の段へ</span>
        </button>
        <h1 className="stamp-book__title">{LABELS.stampBook}</h1>
        <button
          type="button"
          className="stamp-book__header-btn"
          aria-label="設定を開く"
          onClick={openPopover}
        >
          <Icon name="gear" decorative size={20} />
        </button>
        <SettingsPanel />
      </HeaderZone>
      <MainZone className="stamp-book__main">
        <p className="stamp-book__status" aria-live="polite">
          {statusText}
        </p>
        <fieldset className="stamp-book__grid" aria-label="朱印の枠">
          {frames.map((frame, index) => (
            <button
              key={frame.id}
              type="button"
              ref={(el) => {
                buttonRefs.current[index] = el;
              }}
              className={`stamp-book__frame${frame.earned ? " stamp-book__frame--earned" : " stamp-book__frame--blank"}`}
              tabIndex={index === focusedIndex ? 0 : -1}
              aria-label={frameAriaLabel(frame)}
              onClick={() => handleFrameActivate(frame)}
              onKeyDown={(event) => handleFrameKeyDown(event, index)}
              onFocus={() => setFocusedIndex(index)}
            >
              <span
                className="stamp-book__frame-icon"
                style={frame.earned ? { transform: `rotate(${frame.tiltDegrees}deg)` } : undefined}
              >
                <Icon name={frame.earned ? frame.iconName : MON_BLANK_NAME} size={96} decorative />
              </span>
              <span className="stamp-book__frame-label">{frame.label}</span>
            </button>
          ))}
        </fieldset>
        {grandEarned && (
          <div className="stamp-book__cta">
            <Button
              variant="primary"
              keyHint="⏎"
              onClick={() => navigate({ name: "level-select" })}
            >
              稽古の段へ
            </Button>
          </div>
        )}
      </MainZone>
    </>
  );
}
